import type {
    AwsSignedUploadRequest,
    AwsSignedUploadResponse
} from 'x1-base/types';
import type { AwsSignedUploadListRequest } from '~/types';
import { useApiUtils } from 'x1-base/composables';
import { useCustomFetch } from '~/composables';
import { inRange as lo_inRange } from 'es-toolkit';
import type { NitroFetchOptions } from 'nitropack';


export function useFileUploadHandler() {
    const ctx = useNuxtApp();
    const apiBasePath = `/v1`;
    const useApiUtilsObj = useApiUtils();
    const $_fetch = useCustomFetch();

    async function requestUploadUrl(
        url: string,
        body: AwsSignedUploadRequest | AwsSignedUploadListRequest,
        keepErrorDetail = false
    ): Promise<AwsSignedUploadResponse | Error> {
        const config: NitroFetchOptions<string> = {
            method: 'POST',
            body
        };

        try {
            return await $_fetch<AwsSignedUploadResponse>(url, config);
        } catch (err) {
            // `getErrorObj` flattens to the raw ofetch string ('[POST] "...": 404
            // Not Found'), which is no use to a customer. Callers that render the
            // failure keep the status and body instead.
            return keepErrorDetail
                ? useApiUtilsObj.getNuxtErrorObj(err)
                : useApiUtilsObj.getErrorObj(err);
        }
    }

    async function getUploadUrl(type: string, args: AwsSignedUploadRequest): Promise<AwsSignedUploadResponse | Error> {
        return requestUploadUrl(`${apiBasePath}/${type}/${args.uid}/get-upload-url/`, args);
    }

    // Grants for a record that does not exist yet (a review is only written once
    // its photos are already in S3) are issued at the list level, so there is no
    // uid in the path or the body. The response carries a `handle` instead, which
    // the caller sends back when it finally creates the record.
    async function getListUploadUrl(
        type: string,
        args: AwsSignedUploadListRequest
    ): Promise<AwsSignedUploadResponse | Error> {
        return requestUploadUrl(`${apiBasePath}/${type}/get-upload-url/`, args, true);
    }

    async function uploadFile(
        file: File,
        uploadObj: AwsSignedUploadResponse,
        onProgressFn: Nilish<(event: ProgressEvent) => void>
    ): Promise<boolean | Error> {
        try {
            const form = new FormData();

            Object.entries(uploadObj.fields).forEach(([field, value]) => form.append(field, value));
            form.append('file', file, file.name);

            return doXhrUpload(uploadObj.url, form, onProgressFn);
        } catch (err) {
            return useApiUtilsObj.getErrorObj(err);
        }
    }

    // NOTE: Using XHR because currently Fetch's progress capability not fully supported
    // REF: https://developer.chrome.com/articles/fetch-streaming-requests/
    // REF: https://caniuse.com/streams
    async function doXhrUpload(
        url: string,
        form: FormData,
        onProgressFn: Nilish<(event: ProgressEvent) => void>
    ): Promise<boolean | Error> {
        return new Promise((resolve, reject) => {
            const xhr = new XMLHttpRequest();

            xhr.upload.addEventListener('progress', (event: ProgressEvent) => {
                if (event.lengthComputable) {
                    onProgressFn?.(event);
                }
            });

            xhr.addEventListener('load', (event: ProgressEvent) => {
                if (event.lengthComputable) {
                    onProgressFn?.(event);
                }

                if (lo_inRange(xhr.status, 200, 300)) {
                    resolve(true);
                } else {
                    reject(new Error('Error uploading file'));
                }
            });

            xhr.addEventListener('error', () => reject(new Error('Error uploading file')));
            xhr.addEventListener('abort', () => reject(new Error('Upload aborted')));
            xhr.addEventListener('timeout', () => reject(new Error('Upload timed-out')));

            xhr.open('POST', url);
            // NOTE: DO NOT SET THIS: S3 will return MalformedPOSTRequest error
            // xhr.setRequestHeader('Content-Type', 'multipart/form-data');
            xhr.send(form);
        });
    }

    return {
        getUploadUrl,
        getListUploadUrl,
        uploadFile
    };
}
